import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, Check, Handshake, Star, X, UserPlus, Users, Award, Clock, MessageSquare } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const ICONS = {
  new_request: Handshake,
  collab_accepted: Check,
  collab_declined: X,
  new_review: Star,
  review_reminder: Star,
  connection_request: UserPlus,
  connection_accepted: Users,
  invite_joined: UserPlus,
  founding_creator: Award,
  event_reminder: Clock,
  application_accepted: Check,
  new_message: MessageSquare,
};

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(() => {
    if (!user) return;
    api.get("/notifications/unread_count").then((r) => setCount(r.data.count)).catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    if (open) {
      api.get("/notifications").then((r) => setItems(r.data.notifications)).catch(() => {});
    }
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read_all");
    setCount(0);
    setItems((its) => its.map((i) => ({ ...i, read: true })));
  };

  const openItem = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`);
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" data-testid="notification-bell" className="relative rounded-full">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span data-testid="notification-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
          <p className="font-display font-bold text-zinc-900">Notifications</p>
          {items.some((i) => !i.read) && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs font-medium text-emerald-600 hover:underline">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400">No notifications yet</p>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 border-b border-zinc-50 px-4 py-3 text-left transition-colors hover:bg-zinc-50 ${!n.read ? "bg-emerald-50/40" : ""}`}
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{n.title}</p>
                    <p className="text-xs text-zinc-500">{n.body}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
